import type { ElliottWave, FibonacciLevels, HarmonicPattern, PivotPoints } from '../../schemas/indicatortypes.ts';

// Maximum number of datapoints for harmonic patterns (consistent with other indicators)
const MAX_DATAPOINTS = 500;

// Support and Resistance Indicators
export class SupportResistanceIndicators {
    /**
     * Calculate Pivot Points (Classic)
     */
    static calculatePivotPoints(prices: number[], highs: number[], lows: number[]): PivotPoints[] {
        const pivotPoints: PivotPoints[] = [];

        for (let i = 0; i < prices.length; i++) {
            const high = highs[i] || prices[i];
            const low = lows[i] || prices[i];
            const close = prices[i];

            // Pivot Point (PP)
            const pp = (high + low + close) / 3;

            // Support and Resistance levels
            const r1 = 2 * pp - low;
            const s1 = 2 * pp - high;
            const r2 = pp + (high - low);
            const s2 = pp - (high - low);
            const r3 = high + 2 * (pp - low);
            const s3 = low - 2 * (high - pp);

            pivotPoints.push({
                pp,
                r1,
                r2,
                r3,
                s1,
                s2,
                s3,
            });
        }

        return pivotPoints;
    }

    /**
     * Calculate Fibonacci Pivot Points
     */
    static calculateFibonacciPivotPoints(prices: number[], highs: number[], lows: number[]): PivotPoints[] {
        const pivotPoints: PivotPoints[] = [];

        for (let i = 0; i < prices.length; i++) {
            const high = highs[i] || prices[i];
            const low = lows[i] || prices[i];
            const close = prices[i];

            // Pivot Point (PP)
            const pp = (high + low + close) / 3;

            // Fibonacci ratios
            const range = high - low;
            const r1 = pp + 0.382 * range;
            const r2 = pp + 0.618 * range;
            const r3 = pp + 1.0 * range;
            const s1 = pp - 0.382 * range;
            const s2 = pp - 0.618 * range;
            const s3 = pp - 1.0 * range;

            pivotPoints.push({
                pp,
                r1,
                r2,
                r3,
                s1,
                s2,
                s3,
            });
        }

        return pivotPoints;
    }

    /**
     * Calculate Camarilla Pivot Points
     */
    static calculateCamarillaPivotPoints(prices: number[], highs: number[], lows: number[]): PivotPoints[] {
        const pivotPoints: PivotPoints[] = [];

        for (let i = 0; i < prices.length; i++) {
            const high = highs[i] || prices[i];
            const low = lows[i] || prices[i];
            const close = prices[i];

            // Pivot Point (PP)
            const pp = (high + low + close) / 3;

            // Camarilla levels
            const range = high - low;
            const r1 = close + (1.1 / 12) * range;
            const r2 = close + (1.1 / 6) * range;
            const r3 = close + (1.1 / 4) * range;
            const s1 = close - (1.1 / 12) * range;
            const s2 = close - (1.1 / 6) * range;
            const s3 = close - (1.1 / 4) * range;

            pivotPoints.push({
                pp,
                r1,
                r2,
                r3,
                s1,
                s2,
                s3,
            });
        }

        return pivotPoints;
    }

    /**
     * Calculate Fibonacci Levels
     */
    static calculateFibonacciLevels(prices: number[]): FibonacciLevels[] {
        const fibonacci: FibonacciLevels[] = [];
        for (let i = 0; i < prices.length; i++) {
            const price = prices[i];
            fibonacci.push({
                retracement: {
                    level0: price,
                    level236: price * 0.764,
                    level382: price * 0.618,
                    level500: price * 0.5,
                    level618: price * 0.382,
                    level786: price * 0.214,
                    level100: price * 0,
                },
                extension: {
                    level1272: price * 1.272,
                    level1618: price * 1.618,
                    level2618: price * 2.618,
                    level4236: price * 4.236,
                },
            });
        }
        return fibonacci;
    }

    /**
     * Calculate Gann Levels
     */
    static calculateGannLevels(prices: number[]): number[] {
        const gannLevels: number[] = [];

        if (prices.length === 0) return [];

        // If we have more than MAX_DATAPOINTS, sample intelligently
        if (prices.length <= MAX_DATAPOINTS) {
            for (let i = 0; i < prices.length; i++) {
                gannLevels.push(prices[i] * (1 + i * 0.01));
            }
            return gannLevels;
        }

        // Smart sampling to preserve important Gann levels
        const step = prices.length / MAX_DATAPOINTS;

        // Always include the first level
        gannLevels.push(prices[0] * 1.01);

        // Sample levels with smart aggregation
        for (let i = 1; i < MAX_DATAPOINTS - 1; i++) {
            const startIndex = Math.floor(i * step);
            const endIndex = Math.floor((i + 1) * step);
            const segment = prices.slice(startIndex, endIndex);

            if (segment.length === 0) continue;

            // Use the middle price from the segment for the Gann level
            const middleIndex = Math.floor(segment.length / 2);
            const middlePrice = segment[middleIndex];
            const gannLevel = middlePrice * (1 + (startIndex + middleIndex) * 0.01);

            gannLevels.push(gannLevel);
        }

        // Always include the last level
        const lastIndex = prices.length - 1;
        gannLevels.push(prices[lastIndex] * (1 + lastIndex * 0.01));

        return gannLevels;
    }

    /**
     * Calculate Elliott Wave
     */
    static calculateElliottWave(prices: number[]): ElliottWave[] {
        const elliottWave: ElliottWave[] = [];

        if (prices.length < 10) return [];

        // Simple Elliott Wave detection (basic implementation)
        for (let i = 0; i < prices.length; i++) {
            const waves: number[] = [];
            let currentWave = 1;
            let wavePosition = 0.5;

            // Basic wave detection using price swings
            if (i >= 4) {
                const recentPrices = prices.slice(i - 4, i + 1);
                const swings = SupportResistanceIndicators.detectPriceSwings(recentPrices);

                if (swings.length >= 3) {
                    waves.push(...swings.slice(0, 3));
                    currentWave = Math.min(swings.length, 5);
                    wavePosition = SupportResistanceIndicators.calculateWavePosition(recentPrices);
                }
            }

            elliottWave.push({
                waves: waves.length > 0 ? waves : [prices[i]],
                currentWave,
                wavePosition,
            });
        }

        return elliottWave;
    }

    /**
     * Helper method to detect price swings
     */
    private static detectPriceSwings(prices: number[]): number[] {
        const swings: number[] = [];

        for (let i = 1; i < prices.length - 1; i++) {
            const prev = prices[i - 1];
            const curr = prices[i];
            const next = prices[i + 1];

            // Peak detection
            if (curr > prev && curr > next) {
                swings.push(curr);
            }
            // Trough detection
            else if (curr < prev && curr < next) {
                swings.push(curr);
            }
        }

        return swings;
    }

    /**
     * Helper method to calculate wave position
     */
    private static calculateWavePosition(prices: number[]): number {
        if (prices.length < 2) return 0.5;

        const current = prices[prices.length - 1];
        const min = Math.min(...prices);
        const max = Math.max(...prices);

        return max !== min ? (current - min) / (max - min) : 0.5;
    }

    /**
     * Calculate Harmonic Patterns
     */
    static calculateHarmonicPatterns(prices: number[]): HarmonicPattern[] {
        const harmonicPatterns: HarmonicPattern[] = [];

        if (prices.length < 5) return [];

        // Calculate all patterns first
        const allPatterns: HarmonicPattern[] = [];
        for (let i = 4; i < prices.length; i++) {
            const recentPrices = prices.slice(i - 4, i + 1);
            const pattern = SupportResistanceIndicators.detectHarmonicPattern(recentPrices);
            allPatterns.push(pattern);
        }

        // If we have more than MAX_DATAPOINTS, sample intelligently
        if (allPatterns.length <= MAX_DATAPOINTS) {
            return allPatterns;
        }

        // Smart sampling to preserve important patterns
        const step = allPatterns.length / MAX_DATAPOINTS;

        // Always include the first pattern
        harmonicPatterns.push(allPatterns[0]);

        // Sample patterns with smart aggregation
        for (let i = 1; i < MAX_DATAPOINTS - 1; i++) {
            const startIndex = Math.floor(i * step);
            const endIndex = Math.floor((i + 1) * step);
            const segment = allPatterns.slice(startIndex, endIndex);

            if (segment.length === 0) continue;

            // Find the most significant pattern in the segment (highest completion)
            const mostSignificantPattern = segment.reduce((best, current) =>
                current.completion > best.completion ? current : best,
            );

            harmonicPatterns.push(mostSignificantPattern);
        }

        // Always include the last pattern
        harmonicPatterns.push(allPatterns[allPatterns.length - 1]);

        return harmonicPatterns;
    }

    /**
     * Helper method to detect harmonic patterns
     */
    private static detectHarmonicPattern(prices: number[]): HarmonicPattern {
        if (prices.length < 5) {
            return {
                type: 'Unknown',
                completion: 0,
                target: prices[prices.length - 1] * 1.1,
                stopLoss: prices[prices.length - 1] * 0.9,
            };
        }

        // Calculate price swings
        const swings = SupportResistanceIndicators.detectPriceSwings(prices);

        if (swings.length < 4) {
            return {
                type: 'Unknown',
                completion: 0,
                target: prices[prices.length - 1] * 1.1,
                stopLoss: prices[prices.length - 1] * 0.9,
            };
        }

        // Get the last 5 swing points for pattern detection
        const swingPoints = swings.slice(-5);
        if (swingPoints.length < 5) {
            return {
                type: 'Unknown',
                completion: 0,
                target: prices[prices.length - 1] * 1.1,
                stopLoss: prices[prices.length - 1] * 0.9,
            };
        }

        const [X, A, B, C, D] = swingPoints;

        // Calculate Fibonacci ratios
        const AB = Math.abs(B - A);
        const BC = Math.abs(C - B);
        const CD = Math.abs(D - C);
        const XA = Math.abs(A - X);

        const AB_XA_ratio = AB / XA;
        const BC_AB_ratio = BC / AB;
        const CD_BC_ratio = CD / BC;

        // Gartley Pattern (0.618, 0.382, 0.886)
        if (
            SupportResistanceIndicators.isInRange(AB_XA_ratio, 0.618, 0.05) &&
            SupportResistanceIndicators.isInRange(BC_AB_ratio, 0.382, 0.05) &&
            SupportResistanceIndicators.isInRange(CD_BC_ratio, 0.886, 0.05)
        ) {
            return {
                type: 'Gartley',
                completion: SupportResistanceIndicators.calculatePatternCompletion(prices),
                target: D + (D - C) * 0.618,
                stopLoss: D * 0.99,
            };
        }

        // Bat Pattern (0.382, 0.382, 0.886)
        if (
            SupportResistanceIndicators.isInRange(AB_XA_ratio, 0.382, 0.05) &&
            SupportResistanceIndicators.isInRange(BC_AB_ratio, 0.382, 0.05) &&
            SupportResistanceIndicators.isInRange(CD_BC_ratio, 0.886, 0.05)
        ) {
            return {
                type: 'Bat',
                completion: SupportResistanceIndicators.calculatePatternCompletion(prices),
                target: D + (D - C) * 0.382,
                stopLoss: D * 0.99,
            };
        }

        // Butterfly Pattern (0.786, 0.382, 1.618)
        if (
            SupportResistanceIndicators.isInRange(AB_XA_ratio, 0.786, 0.05) &&
            SupportResistanceIndicators.isInRange(BC_AB_ratio, 0.382, 0.05) &&
            SupportResistanceIndicators.isInRange(CD_BC_ratio, 1.618, 0.05)
        ) {
            return {
                type: 'Butterfly',
                completion: SupportResistanceIndicators.calculatePatternCompletion(prices),
                target: D + (D - C) * 1.618,
                stopLoss: D * 0.99,
            };
        }

        // Crab Pattern (0.382, 0.886, 3.618)
        if (
            SupportResistanceIndicators.isInRange(AB_XA_ratio, 0.382, 0.05) &&
            SupportResistanceIndicators.isInRange(BC_AB_ratio, 0.886, 0.05) &&
            SupportResistanceIndicators.isInRange(CD_BC_ratio, 3.618, 0.1)
        ) {
            return {
                type: 'Crab',
                completion: SupportResistanceIndicators.calculatePatternCompletion(prices),
                target: D + (D - C) * 1.618,
                stopLoss: D * 0.99,
            };
        }

        // Cypher Pattern (0.382, 0.113, 1.414)
        if (
            SupportResistanceIndicators.isInRange(AB_XA_ratio, 0.382, 0.05) &&
            SupportResistanceIndicators.isInRange(BC_AB_ratio, 0.113, 0.05) &&
            SupportResistanceIndicators.isInRange(CD_BC_ratio, Math.SQRT2, 0.05)
        ) {
            return {
                type: 'Cypher',
                completion: SupportResistanceIndicators.calculatePatternCompletion(prices),
                target: D + (D - C) * 0.786,
                stopLoss: D * 0.99,
            };
        }

        // Shark Pattern (0.886, 0.886, 1.13)
        if (
            SupportResistanceIndicators.isInRange(AB_XA_ratio, 0.886, 0.05) &&
            SupportResistanceIndicators.isInRange(BC_AB_ratio, 0.886, 0.05) &&
            SupportResistanceIndicators.isInRange(CD_BC_ratio, 1.13, 0.05)
        ) {
            return {
                type: 'Shark',
                completion: SupportResistanceIndicators.calculatePatternCompletion(prices),
                target: D + (D - C) * 1.13,
                stopLoss: D * 0.99,
            };
        }

        return {
            type: 'Unknown',
            completion: 0,
            target: prices[prices.length - 1] * 1.1,
            stopLoss: prices[prices.length - 1] * 0.9,
        };
    }

    /**
     * Helper method to check if a value is within a range
     */
    private static isInRange(value: number, target: number, tolerance: number): boolean {
        return Math.abs(value - target) <= tolerance;
    }

    /**
     * Helper method to calculate pattern completion percentage
     */
    private static calculatePatternCompletion(prices: number[]): number {
        if (prices.length < 2) return 0;

        const current = prices[prices.length - 1];
        const max = Math.max(...prices);
        const min = Math.min(...prices);

        if (max === min) return 50;

        const range = max - min;
        const position = (current - min) / range;
        return Math.min(100, Math.max(0, position * 100));
    }
}
